/**
 * BottomActionBar — PROD-1194. The ONE place a step screen's primary button lives: pinned to the
 * bottom of the visible screen while the content above it scrolls.
 *
 * Alex (2026-10-19): on sign-up, setup and the PIN step the primary action (Continue, Set PIN,
 * Next, Send code ...) sat at the END of the content, so on a small iPhone it was below the fold
 * ("Create a quick-access PIN" 1/3: Set PIN only after scrolling past the keypad).
 *
 * HOW (index.css `.bottom-action-bar`, next to PROD-1176's `.bottom-actions`):
 *  - `position: sticky; bottom: 0` inside the app's one scroller (.scroll-wrapper), with
 *    `margin-top: auto`, so on a short page it sits at the bottom of the page's flex column and on
 *    a long one it stays at the bottom of the screen. The pattern ViewSummary's footer ships.
 *  - It pays the home-indicator inset itself through `.bottom-actions` (floor 14px). Anything that
 *    wraps it must not pay it again (OnboardingFrame and ResponsiveWrapper are told not to:
 *    check-viewport-bottom-actions A6 / A10).
 *  - `aboveTabBar`: on a MobileLayout route the TabBar is fixed at the bottom; the bar then sits on
 *    top of it with the same lift MobileLayout's <main> reserves, and pays no inset (the TabBar does).
 *  - The keyboard (web only): useKeyboardInset lifts the bar above iPhone Safari's keyboard.
 *  - A disabled primary stays visible (its own greyed style); the bar never hides.
 *
 * Props: children (the button(s); a submit button stays inside its <form>, so the bar goes inside
 * the form), note (a line pinned above the button, e.g. an error), border (top hairline, default
 * true), aboveTabBar, className.
 * ★ A parent with `transform`, `overflow` other than visible, or a fixed height between this bar and
 *   .scroll-wrapper breaks `sticky`; the render gate (check-browser-render R27) measures the bar on
 *   the real pages.
 */
import useKeyboardInset from '../hooks/useKeyboardInset'

export default function BottomActionBar({ children, note = null, border = true, aboveTabBar = false, className = '', ...rest }) {
  useKeyboardInset()
  return (
    <div
      data-bottom-action-bar
      data-tabbar={aboveTabBar ? '1' : undefined}
      data-border={border ? '1' : undefined}
      className={`bottom-action-bar bottom-actions ${className}`}
      {...rest}
    >
      {note}
      {children}
    </div>
  )
}
